import { useState, useEffect, useCallback } from 'react';
import { getPasswordChangeStatus, passwordChangeRequiresChange } from '../_services/passwordChange.service';
import { getPasskeyPopupStatus, passkeyStatusShouldShowPopup } from '../_services/passkey.service';

export const POPUP_KEYS = {
    CHANGE_PASSWORD: 'CHANGE_PASSWORD',
    COMPLIANCE:      'COMPLIANCE',
    CREATE_PASSKEY:  'CREATE_PASSKEY',
};

const PRIORITY = {
    [POPUP_KEYS.CHANGE_PASSWORD]: 1,
    [POPUP_KEYS.CREATE_PASSKEY]:  2,
    [POPUP_KEYS.COMPLIANCE]:      3,
};

/**
 * Fetches all post-login popup statuses in parallel and exposes an ordered queue.
 *
 * Priority order: P1 password nudge → P2 passkey → P3 compliance.
 * The queue only includes steps that are actually needed.
 * The client never re-computes 60-day logic; it trusts requiresChange from the API.
 *
 * @param {{ id: number, userrole: string, iscompopup: boolean } | null} user
 * @returns {{ activePopup: string | null, dismissCurrent: () => void, loading: boolean }}
 */
export function useSystemPopupQueue(user) {
    const [queue, setQueue] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user?.id) {
            setQueue([]);
            setCurrentIndex(0);
            setLoading(false);
            return;
        }

        let cancelled = false;

        async function fetchAllStatuses() {
            setLoading(true);
            const pending = [];

            try {
                const [pwdStatus, passkeyStatus] = await Promise.all([
                    getPasswordChangeStatus(user.id, user.userrole || '').catch(() => null),
                    // Only fetch passkey status if WebAuthn is available
                    (typeof window !== 'undefined' && window.PublicKeyCredential)
                        ? getPasskeyPopupStatus(user.id).catch(() => null)
                        : Promise.resolve(null),
                ]);

                if (cancelled) return;

                // P1 — password nudge (server decides; client only reads the boolean)
                if (passwordChangeRequiresChange(pwdStatus)) {
                    pending.push(POPUP_KEYS.CHANGE_PASSWORD);
                }

                // P2 — passkey promo (server-driven, WebAuthn must be supported)
                if (
                    typeof window !== 'undefined' &&
                    window.PublicKeyCredential &&
                    passkeyStatusShouldShowPopup(passkeyStatus)
                ) {
                    pending.push(POPUP_KEYS.CREATE_PASSKEY);
                }

                // P3 — compliance (derived from login payload already in memory)
                if (user.userrole === 'Wholesaler' && user.iscompopup === false) {
                    pending.push(POPUP_KEYS.COMPLIANCE);
                }

                // Sort by priority (lowest number shows first)
                pending.sort((a, b) => PRIORITY[a] - PRIORITY[b]);
            } catch {
                // On error, show nothing rather than crashing
            }

            if (!cancelled) {
                setQueue(pending);
                setCurrentIndex(0);
                setLoading(false);
            }
        }

        fetchAllStatuses();

        return () => {
            cancelled = true;
        };
    }, [user?.id]); // Re-run only when the logged-in user changes

    const dismissCurrent = useCallback(() => {
        setCurrentIndex((i) => i + 1);
    }, []);

    return {
        activePopup: queue[currentIndex] ?? null,
        dismissCurrent,
        loading,
    };
}
